import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetUsersListQuery,
  GetUsersListData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetUsersListState
  extends MergeRequestState<GetUsersListData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getUsersList,
  isFetching: (state: ReduxState) => state.server.getUsersList.isFetching,
  data: (state: ReduxState) => state.server.getUsersList.data,
  error: (state: ReduxState) => state.server.getUsersList.error,
};

const initialState: GetUsersListState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getUsersList';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { query, config }: { query: GetUsersListQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getUsersList(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetUsersList {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getUsersList: ServerGetUsersList = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
